import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spinner, RadioButton } from '@gsa/afp-component-library';
import HorizontalPagination from '../../../components/HorizontalPagination/HorizontalPagination';
import ContractImagesSection from '@/components/compare-select/contract-images-section.jsx';
import MinimumRequirementsSection from '../../../components/MinimumRequirementsSection/MinimumRequirementsSection';
import {
  getDisplayableMinimumRequirements,
  getAvailableMinimumRequirements,
  getDisplayableNetworkCostBreakDownData,
  getDisplayableVehicleInfo,
  getDisplayableVehicleOverview,
  getDisplayableVehicleEngFuel,
  getVehicleOverviewInformation,
  getAvailableVehicleInformation,
  getGroupedOptionalRequirementsData,
  getDisplayableContracts,
  getSimplifiedContractData,
  getSortedActiveContractsDataForLeasing,
} from '../../../utilities/CompareVehicleUtils';
import {
  setActiveEngineInfo,
  setAreqViewMode,
  setPagination,
  setSelectedOptions,
  setSimplifiedContractsData,
  setVendorContractsCostBreakdown,
  setVendorSinContracts,
  setViewOptionsPricingMode,
} from '../../../reducers/review';
import BasePrice from '../../../components/BasePrice/BasePrice';
import VehicleInformationAccordion from '../../../components/VehicleInformationSection/VehicleInformationSection';
import CostBreakdownSection from '../../../components/CostBreakdownSection/CostBreakdownSection';
import AddOptionsSection from './add-options';
import TotalSellingPriceSection from './total-selling-price-leasing/total-selling-price';
import './areq-compare-and-select.scss';
import { useGetCollisionsForSelection } from '../../../requests/leasing.jsx';
import CloseoutDate from '../../../components/CloseoutDates/CloseoutDate.jsx';
import VendorClarification from '../../../components/VendorClarifications/VendorClarification.jsx';
import {
  limitDeliveryOptionsByState,
  limitDeliveryOptionsByCountry,
} from '../../../components/AddOptions/AddOptionsUtils.jsx';
import {
  useGetContractsCostBreakdown2, useGetSinContracts
} from '../../../requests/common-requests';
import {
  AreqViewMode,
  ViewOptionsPricingMode,
} from '../../VehicleRequisition/constants/VehicleRequisitionConstants';
import { withoutAREQOption } from '../../../constants/utils';

const { appURLs } = window.AFP_CONFIG;

const pricingModeOptions = [
  {
    label: 'Show contract prices',
    value: ViewOptionsPricingMode.contract,
    default: true,
  },
  {
    label: 'Show customer prices (includes purchase rates)',
    value: ViewOptionsPricingMode.customer,
    default: false,
  },
];

const AreqCompareSelect = () => {
  const {
    minOptions,
    optionalRequirements,
    contractsCostBreakdown,
    vendorContractsCostBreakdown,
    simplifiedContractsData,
    activeEngineInfo,
    sinContracts,
    vendorSinContracts,
    featureOptions,
    pagination,
    standardItem,
    requisition,
    viewOptionsPricingMode,
  } = useSelector((state) => state.reviewReducer);

  // hardcoded
  const destinationType = 'us-territory';
  const shipmentState = 'US-NC';
  const shipmentCountry = 'US';


  const dispatch = useDispatch();
  const [vehicleBodyIcon, setVehicleBodyIcon] = useState(null);
  const [getCollisionsForYes, { loading: isCollisionsLoadingForYes }] =
    useGetCollisionsForSelection();

  const activeContracts = useMemo(() => {
    if (viewOptionsPricingMode === ViewOptionsPricingMode.contract) {
      return vendorSinContracts;
    } else {
      return sinContracts;
    }
  }, [viewOptionsPricingMode, sinContracts, vendorSinContracts]);

  const readOnlyCostBreakdown = useMemo(() => {
    if (viewOptionsPricingMode === ViewOptionsPricingMode.contract) {
      return vendorContractsCostBreakdown;
    } else {
      return contractsCostBreakdown;
    }
  }, [
    viewOptionsPricingMode,
    contractsCostBreakdown,
    vendorContractsCostBreakdown,
  ]);

  const shipmentCategory = useMemo(() => {
    if (destinationType === 'us-territory') {
      return shipmentState;
    } else if (destinationType === 'foreign-county') {
      return 'EXPORT';
    } else {
      return 'ExDel';
    }
  }, [destinationType, shipmentState]);
  /* data massage for sections */
  const sortedActiveContractsData = useMemo(() => {
    console.log({activeContracts, readOnlyCostBreakdown});
    if (activeContracts?.length && readOnlyCostBreakdown)
      return getSortedActiveContractsDataForLeasing(
        activeContracts,
        readOnlyCostBreakdown,
      );
    else return [];
  }, [activeContracts, readOnlyCostBreakdown]);

  const [getVendorSinContracts] = useGetSinContracts(setVendorSinContracts);
  const [getVendorContractsCostBreakdown] = useGetContractsCostBreakdown2(
    setVendorContractsCostBreakdown,
  );

  useMemo(() => {
    const [simplifiedContractsData, activeEngineInfo] =
      getSimplifiedContractData(sortedActiveContractsData);
    dispatch(setSimplifiedContractsData(simplifiedContractsData));
    dispatch(setActiveEngineInfo(activeEngineInfo));
  }, [sortedActiveContractsData]);

  const lowBidContract = simplifiedContractsData
    ? simplifiedContractsData[0]
    : null;

  const allAvailableVehicleInformation = useMemo(() => {
    return getAvailableVehicleInformation(
      featureOptions,
      simplifiedContractsData,
    );
  }, [featureOptions, simplifiedContractsData]);

  const allAvailableVehicleOverview = useMemo(() => {
    return simplifiedContractsData?.length > 0
      ? getVehicleOverviewInformation(simplifiedContractsData)
      : [];
  }, [simplifiedContractsData]);

  const minimumRequirementData = useMemo(() => {
    const data = getAvailableMinimumRequirements(
      minOptions,
      simplifiedContractsData,
    );
    return data;
  }, [simplifiedContractsData, minOptions]);

  const { allAvailableOptionalReqData, deliveryOptions } = useMemo(() => {
    return getGroupedOptionalRequirementsData(
      optionalRequirements,
      simplifiedContractsData,
      shipmentCategory,
    );
  }, [
    JSON.stringify(optionalRequirements),
    JSON.stringify(simplifiedContractsData),
    shipmentCategory,
  ]);

  /* All sections pagionation handles here */
  const paginatedContracts = useMemo(() => {
    return simplifiedContractsData?.length > 0
      ? getDisplayableContracts(
          pagination.page,
          pagination.offset,
          simplifiedContractsData,
        )
      : [];
  }, [simplifiedContractsData, pagination]);

  const allAvailableMinimumRequirements = useMemo(() => {
    return getDisplayableMinimumRequirements(
      pagination.page,
      pagination.offset,
      minimumRequirementData,
    );
  }, [minimumRequirementData, pagination]);

  const paginatedCost = useMemo(() => {
    return readOnlyCostBreakdown?.length > 0
      ? getDisplayableNetworkCostBreakDownData(
          pagination.page,
          pagination.offset,
          readOnlyCostBreakdown,
        )
      : [];
  }, [readOnlyCostBreakdown, pagination]);

  const displayableOtherFeatures = useMemo(() => {
    return allAvailableVehicleInformation?.length > 0
      ? getDisplayableVehicleInfo(
          pagination.page,
          pagination.offset,
          allAvailableVehicleInformation,
        )
      : [];
  }, [allAvailableVehicleInformation, pagination]);

  const displayableOverview = useMemo(() => {
    return allAvailableVehicleOverview?.length > 0
      ? getDisplayableVehicleOverview(
          pagination.page,
          pagination.offset,
          allAvailableVehicleOverview,
        )
      : [];
  }, [allAvailableVehicleOverview, pagination]);

  const displayableVehicleEngFuel = useMemo(() => {
    return simplifiedContractsData?.length > 0
      ? getDisplayableVehicleEngFuel(
          pagination.page,
          pagination.offset,
          activeEngineInfo,
        )
      : [];
  }, [activeEngineInfo, pagination]);

  useMemo(() => {
    if (requisition) {
      const options =
        requisition?.clientData?.selectedOptions?.selectedOptions?.map((x) => ({
          optionCode: x.optionCode,
          isChecked: true,
          optionQuantity: 1, // TODO Fix
        }));
      dispatch(setSelectedOptions(options));
    }
  }, [requisition]);

  useMemo(() => {
    if (standardItem?.id) {
      dispatch(setViewOptionsPricingMode(ViewOptionsPricingMode.contract));
      getVendorSinContracts({
        variables: {
          standardItemId: +standardItem?.id,
          purchaseRate: 1,
        },
      });

      getVendorContractsCostBreakdown({
        variables: {
          standardItemId: requisition.standardItemId,
          quantity: requisition.quantity,
          selectedOptions: withoutAREQOption(
            requisition.clientData.selectedOptions.selectedOptions,
          ),
          transactionType: requisition.transactionType,
          purchaseRate: 1,
        },
      });

      const standardItemSubCategoryCode = standardItem?.vehicleType;
      const svgCode = standardItemSubCategoryCode?.concat('_black');
      const iconURL = `${appURLs.cdnUrl}/icons/sin-sub-category/${svgCode}.svg`;
      setVehicleBodyIcon(iconURL);
    }
  }, [standardItem]);

  const handleCompareVehiclesPagination = (value) => {
    dispatch(setPagination({ page: value, offset: pagination.offset }));
  };

  const handleImageError = () => {
    const iconURL = `${appURLs.cdnUrl}/icons/sin-sub-category/02.svg`;
    setVehicleBodyIcon(iconURL);
  };

  let limitDeliveryOptions = limitDeliveryOptionsByState(
    deliveryOptions,
    shipmentState,
  );
  if (shipmentCountry === 'IT' || shipmentCountry === 'DE')
    limitDeliveryOptions = limitDeliveryOptionsByCountry(
      deliveryOptions,
      limitDeliveryOptions,
      shipmentCountry,
    );

  const onViewOptionsPricingMode = (e) => {
    const value = e.target.value;
    dispatch(setViewOptionsPricingMode(value));
  };

  const returnToReviewSummary = () => {
    dispatch(setAreqViewMode(AreqViewMode.reviewSummary));
  };

  const loading = false;

  return (
    <div className="compare-vehicles">
      {loading ? (
        <div className="afp-modal-overlay modalContainer conflict-modal">
          <Spinner size="large" className="margin-y-8" />
        </div>
      ) : (
        <>
          <div className="leasing-compare-select-header-section">
            <div className="store-compare-vehicles-sub-header">
              <img
                data-testid="store-compare-sub-header-icon"
                className="store-compare-vehicles-sub-header-image"
                src={vehicleBodyIcon}
                alt="ambulance icon"
                onError={() => {
                  handleImageError();
                }}
              />
              <h3 data-testid="compare-vehicles-sub-header-heading">
                {standardItem?.standardItemNumber} - {standardItem?.title}
              </h3>
            </div>
          </div>
          <div className="page-navigation-section-read-only">
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => returnToReviewSummary()}
                data-testid="store-compare-vehicles-return-to-review-summary-top"
                aria-label="Return to review summary"
                leftIcon={{ name: 'arrow_back', type: 'custom' }}
                label="Return to review summary"
              />
            </div>

            <div className="pricing-mode-row">
              <div className="inline-radio-group">
                {pricingModeOptions.map((option) => (
                  <RadioButton
                    key={`pricing-mode-${option.value}`}
                    className="inline-radio"
                    name="view-options-pricing-mode"
                    data-testid={`pricing-mode-${option.value}`}
                    checked={viewOptionsPricingMode === option.value}
                    onChange={(e) => {
                      onViewOptionsPricingMode(e);
                    }}
                    {...option}
                  />
                ))}
              </div>
              <Button
                type="button"
                variant="solid"
                className="edit-options-button"
                onClick={() => {}}
                data-testid="store-compare-vehicles-edit-options"
                aria-label="Edit options"
                leftIcon={{ name: 'edit', type: 'custom' }}
                label="Edit options"
              />
            </div>
          </div>
          <div className="page-navigation-section-second-row">
            <div></div>

            <div>
              <HorizontalPagination
                offset={pagination.offset}
                id="store-compare-vehicles"
                currentPage={pagination.page}
                handlePageChange={handleCompareVehiclesPagination}
                totalNumberOfResults={simplifiedContractsData?.length}
              />
            </div>
          </div>
          {!!paginatedContracts?.length && (
            <>
              <ContractImagesSection
                contracts={paginatedContracts}
                lowBidContract={lowBidContract}
                quantity={requisition?.quantity}
              />
              <CloseoutDate
                contracts={paginatedContracts}
                lowestBidData={lowBidContract}
              />
              <VendorClarification
                contracts={paginatedContracts}
                lowestBidData={lowBidContract}
              />
              <MinimumRequirementsSection
                minimumRequirementsData={allAvailableMinimumRequirements}
                lowestBidData={lowBidContract}
              />
              <VehicleInformationAccordion
                vehicleInformationData={{
                  vehicleOtherFeaturesData: displayableOtherFeatures,
                  vehicleOverviewData: displayableOverview,
                  vehicleEngFuelData: displayableVehicleEngFuel,
                }}
                lowestBidData={lowBidContract}
              />
              <BasePrice
                contracts={paginatedContracts}
                lowestBidData={lowBidContract}
                networkCostBreakDownData={paginatedCost}
              />
              {/* {limitDeliveryOptions && limitDeliveryOptions.length > 0 && (
                <DeliveryOptionsSection
                  deliveryOptions={limitDeliveryOptions}
                  pagination={pagination}
                  handleRowCheckBoxClick={() => {}}
                />
              )} */}
              <AddOptionsSection
                allAvailableOptionalReqData1={allAvailableOptionalReqData}
                lowestBidData={lowBidContract}
              />
              <CostBreakdownSection
                lowestBidData={lowBidContract}
                networkCostBreakDownData={paginatedCost}
              />
              <TotalSellingPriceSection
                contracts={paginatedContracts}
                lowestBidData={lowBidContract}
                networkCostBreakDownData={paginatedCost}
                deliveryOptions={limitDeliveryOptions}
              />
              <div className="bottom-return">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => returnToReviewSummary()}
                  data-testid="store-compare-vehicles-return-to-review-summary-bottom"
                  aria-label="Return to review summary"
                  leftIcon={{ name: 'arrow_back', type: 'custom' }}
                  label="Return to review summary"
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AreqCompareSelect;
